import { AudioTrackData } from "./types";
import { dubSoundingMs, tapeOrder, voicingSounds, wholeMs } from "./dubSounding";

export interface AdjustAllFragmentsOptions {
    /**
     * Только настоящие наложения — режим «Без автоправок» (правило владельца 01.10.2026: «ты
     * должен всегда сдвигать соседние реплики иначе будут наслоения»). Озвучка сдвигается, только
     * если начинается РАНЬШЕ конца предыдущей озвучки, и встаёт на этот конец + `gapMs` — та же
     * посадка, что у обычной раскладки. Озвучка, стоящая вплотную или ближе `gapMs`, но не
     * наезжающая, остаётся на месте: её место — работа переводчика, а не наслоение.
     * Мерка — та же, что у сервера и сборки (контракт 01.10.2026): сколько звучит озвучка —
     * `dub_ms` сервера (`dubSoundingMs`), места сравниваются в целых мс.
     * Без флага — прежняя раскладка: сдвигается и всё, что ближе `gapMs` к предыдущей.
     */
    onlyOverlaps?: boolean;
}

export function adjustAllFragments(
    tracks: AudioTrackData[],
    gapMs: number = 50,
    { onlyOverlaps = false }: AdjustAllFragmentsOptions = {},
): { shifted: AudioTrackData[]; shiftedIds: string[] } {
    // Sort EVERY cue, voiced or not. prepareData replaces the whole track array with
    // `shifted`, so a cue dropped here vanishes from the voiceover column altogether and
    // the translator never sees the "no audio yet" placeholder — the column just silently
    // gets shorter and the missing voicings look like they were never requested.
    // Only cues that actually carry audio take part in the overlap chain below.
    if (onlyOverlaps) {
        return pushOnlyOverlaps(tracks, gapMs);
    }
    const sorted = [...tracks].sort((a, b) => a.start - b.start);
    const shiftedIds: string[] = [];
    let prevEnd: number | null = null;

    for (const track of sorted) {
        // No file → nothing plays for this cue, so it neither collides with the previous
        // voicing nor pushes the next one later. Так же и у снятой с озвучки (⊘): её старый
        // `file` остаётся на записи, но в видео её дубля нет (voicingSounds).
        if (!voicingSounds(track)) {
            continue;
        }
        let start = track.start;
        const length = track.length;
        if (prevEnd !== null && start < prevEnd + gapMs) {
            start = prevEnd + gapMs;
            moveLater(track, start);
            shiftedIds.push(track.id);
        }
        prevEnd = start + length;
    }
    return { shifted: sorted, shiftedIds };
}

/**
 * Поставить озвучку на `to` (позже прежнего места): start — ровно туда, shifted_start и
 * new_start — на ту же дельту. new_start считается от СВОЕГО прежнего значения, а без него (0
 * или нет поля) — от прежнего start: раньше его брали от уже сдвинутого start, и он уезжал на
 * сдвиг дважды. Нет shifted_start — его и нет (раньше становился NaN).
 */
function moveLater(track: AudioTrackData, to: number) {
    const was = track.start;
    const shift = to - was;
    track.start = to;
    if (typeof track.shifted_start === "number") track.shifted_start += shift;
    track.new_start = (track.new_start || was) + shift;
}

/**
 * «Без автоправок»: снять наложения сдвигом позже — и больше ничего. Порядок ленты — как у
 * сервера и сборки (`tapeOrder`): по месту как есть, при равенстве — по id; целые мс
 * (int(start)) — только для сравнения с концом предыдущей. Озвученная реплика (`dubSoundingMs`
 * не null) двигается, только если её место раньше конца предыдущей озвучки (start < prev_end),
 * и встаёт ровно на prev_end + `gapMs`; дальше цепочкой, пока следующая наезжает. Раньше ничего
 * не уезжает, зазоры не закрываются, длина (и скорость) озвучки не трогается.
 */
function pushOnlyOverlaps(tracks: AudioTrackData[], gapMs: number) {
    const sorted = [...tracks].sort(tapeOrder);
    const shiftedIds: string[] = [];
    let prevEnd: number | null = null;

    for (const track of sorted) {
        const sounding = dubSoundingMs(track);
        if (sounding === null || typeof track.start !== "number" || !Number.isFinite(track.start)) {
            continue;
        }
        let start = wholeMs(track.start);
        if (prevEnd !== null && start < prevEnd) {
            start = prevEnd + gapMs;
            moveLater(track, start);
            shiftedIds.push(track.id);
        }
        prevEnd = start + sounding;
    }
    return { shifted: sorted, shiftedIds };
}
